import React from "react";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  estLibelle,
  estNombre,
  formatNombre,
} from "./_commun";

// Repartition — de quoi un total est fait, EN CASCADE.
//
// Ce qu'elle apprend (registre) : « la part de chaque poste dans un total, en
// cascade — pas en tableau ». La facturation montre les lignes ; elle ne
// montre pas le POIDS relatif de chacune. Ici : chaque poste est un segment
// posé à la suite du précédent, en escalier, avec sa part en % écrite.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     unite?: "€" | string,
//     total?: 3200,                                   // facultatif ; s'il est donné, il DOIT égaler Σ parts
//     parts:  [ { label: "Nuits", v: 2100 }, … ]      // ≥ 1 part, v fini ≥ 0, Σ > 0
//   }
//
// ⚠️ Un total fourni qui ne vaut pas la somme des parts (à 0,5 près) refuse le
// dessin : une répartition qui ne fait pas 100 % est une mesure fausse.
// Les parts sont dessinées dans l'ordre fourni (l'appelant décide de l'ordre).

const LIGNE = 24;
const MARGE = { gauche: 92, droite: 54, haut: 8, bas: 8 };
const TOLERANCE = 0.5;

function validerRepartition(d) {
  if (
    !d ||
    typeof d !== "object" ||
    !Array.isArray(d.parts) ||
    d.parts.length === 0
  )
    return { erreur: "pas de données sur cette période" };
  if (
    !d.parts.every(
      (p) =>
        p &&
        typeof p === "object" &&
        estLibelle(p.label) &&
        estNombre(p.v) &&
        p.v >= 0,
    )
  )
    return {
      erreur:
        "données incomplètes — un poste n'a pas de montant (ou il est négatif)",
    };
  const somme = d.parts.reduce((s, p) => s + p.v, 0);
  if (!(somme > 0)) return { erreur: "le total est nul — rien à répartir" };
  if (d.total != null) {
    if (!estNombre(d.total))
      return { erreur: "le total fourni n'est pas un nombre" };
    if (Math.abs(d.total - somme) > TOLERANCE)
      return {
        erreur: `les postes ne font pas le total (Σ ${formatNombre(somme, d.unite)} pour ${formatNombre(d.total, d.unite)} annoncés) — rien dessiné`,
      };
  }
  return { parts: d.parts, total: somme, unite: d.unite };
}

export default function Repartition({ titre, donnees }) {
  const v = validerRepartition(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { parts, total, unite } = v;

  const H = MARGE.haut + LIGNE * parts.length + MARGE.bas;
  const largeurUtile = LARGEUR - MARGE.gauche - MARGE.droite;
  const largeur = (val) => (val / total) * largeurUtile;
  const idxMax = parts.reduce((m, p, i) => (p.v > parts[m].v ? i : m), 0);

  const pct = (val) => Math.round((val / total) * 100);
  const detail = parts.map((p) => `${p.label} ${pct(p.v)} %`).join(", ");
  const conclusion = `${titre} : ${formatNombre(total, unite)} — ${detail}`;

  let cumul = 0;
  return (
    <Figure
      forme="repartition"
      titre={titre}
      conclusion={conclusion}
      hauteur={H}
    >
      {parts.map((p, i) => {
        const x0 = MARGE.gauche + largeur(cumul);
        const w = largeur(p.v);
        const yc = MARGE.haut + LIGNE * i + LIGNE / 2;
        cumul += p.v;
        const principal = i === idxMax;
        return (
          <g
            key={`${p.label}-${i}`}
            data-testid={
              principal ? "repartition-principal" : "repartition-part"
            }
          >
            <text
              x={MARGE.gauche - 8}
              y={yc + 4}
              textAnchor="end"
              fontSize="11"
              fill={COULEUR.navyD}
              fontWeight={principal ? "600" : "400"}
            >
              {p.label}
            </text>
            <rect
              x={x0}
              y={yc - 8}
              width={Math.max(w, 1)}
              height={16}
              fill={principal ? COULEUR.navy : COULEUR.ciel}
              rx="2"
            />
            <text
              x={x0 + w + 6}
              y={yc + 4}
              fontSize="10.5"
              fontWeight="600"
              fill={COULEUR.navyD}
            >
              {`${pct(p.v)} %`}
            </text>
            {/* La marche de l'escalier : la fin d'un poste est le début du suivant. */}
            {i < parts.length - 1 ? (
              <line
                x1={x0 + w}
                x2={x0 + w}
                y1={yc + 8}
                y2={yc + LIGNE - 8}
                stroke={COULEUR.grisD}
                strokeDasharray="2 2"
              />
            ) : null}
          </g>
        );
      })}
      {/* Le bord du total, pour voir que la cascade le remplit. */}
      <line
        x1={MARGE.gauche + largeurUtile}
        x2={MARGE.gauche + largeurUtile}
        y1={MARGE.haut}
        y2={H - MARGE.bas}
        stroke={COULEUR.bord}
      />
    </Figure>
  );
}
